import { z } from "zod";
import type { ApiUsageItemDto } from "./api-costs";

// The workspace wiki (ADR 0075): pages in a tree, text in Markdown or
// question/answer pairs, each one used by the AI always, when relevant or never.

export const WIKI_PAGE_KINDS = ["text", "qa"] as const;
export type WikiPageKind = (typeof WIKI_PAGE_KINDS)[number];

export const WIKI_AI_USAGES = ["always", "relevant", "never"] as const;
export type WikiAiUsage = (typeof WIKI_AI_USAGES)[number];

export const WIKI_AI_USAGE_LABEL: Record<WikiAiUsage, string> = {
  always: "Sempre",
  relevant: "Quando serve",
  never: "Mai",
};

export const WIKI_TITLE_MAX_LENGTH = 120;
export const WIKI_TEXT_MAX_LENGTH = 20_000;
export const WIKI_QA_MAX_ITEMS = 100;
export const WIKI_QUESTION_MAX_LENGTH = 300;
export const WIKI_ANSWER_MAX_LENGTH = 2_000;
/** A page and its sub-pages: at most four levels. */
export const WIKI_MAX_DEPTH = 4;
export const WIKI_MAX_PAGES = 500;
/** All the "Sempre" pages together: they go in every proposal. */
export const WIKI_ALWAYS_MAX_LENGTH = 30_000;
export const WIKI_REVISIONS_KEPT = 50;

export const wikiQaItemSchema = z
  .object({
    id: z.string().uuid(),
    question: z.string().trim().min(1).max(WIKI_QUESTION_MAX_LENGTH),
    answer: z.string().trim().min(1).max(WIKI_ANSWER_MAX_LENGTH),
  })
  .strict();
export type WikiQaItem = z.infer<typeof wikiQaItemSchema>;

const wikiItemsSchema = z.array(wikiQaItemSchema).max(WIKI_QA_MAX_ITEMS);

export const wikiPageCreateSchema = z
  .object({
    parentId: z.string().uuid().nullable(),
    kind: z.enum(WIKI_PAGE_KINDS),
    title: z.string().trim().min(1).max(WIKI_TITLE_MAX_LENGTH),
    aiUsage: z.enum(WIKI_AI_USAGES).default("relevant"),
    body: z.string().trim().max(WIKI_TEXT_MAX_LENGTH).default(""),
    items: wikiItemsSchema.default([]),
  })
  .strict();
export type WikiPageCreate = z.input<typeof wikiPageCreateSchema>;

/** Only what is sent changes; `version` is the one the editor started from. */
export const wikiPageUpdateSchema = z
  .object({
    version: z.number().int().min(1),
    title: z.string().trim().min(1).max(WIKI_TITLE_MAX_LENGTH).optional(),
    aiUsage: z.enum(WIKI_AI_USAGES).optional(),
    body: z.string().trim().max(WIKI_TEXT_MAX_LENGTH).optional(),
    items: wikiItemsSchema.optional(),
  })
  .strict();
export type WikiPageUpdate = z.infer<typeof wikiPageUpdateSchema>;

/** `index` is the place among the new sisters, 0 = first. */
export const wikiPageMoveSchema = z
  .object({
    parentId: z.string().uuid().nullable(),
    index: z.number().int().min(0).max(WIKI_MAX_PAGES),
  })
  .strict();
export type WikiPageMove = z.infer<typeof wikiPageMoveSchema>;

export const wikiRestoreSchema = z.object({ version: z.number().int().min(1) }).strict();

export type WikiPageSummaryDto = {
  id: string;
  parentId: string | null;
  position: number;
  kind: WikiPageKind;
  title: string;
  aiUsage: WikiAiUsage;
  version: number;
  updatedAt: string;
};

export type WikiTreeDto = {
  pages: WikiPageSummaryDto[];
  canEdit: boolean;
  /** Characters of all the "Sempre" pages, against WIKI_ALWAYS_MAX_LENGTH. */
  alwaysLength: number;
};

export type WikiPageDto = WikiPageSummaryDto & {
  body: string;
  items: WikiQaItem[];
  createdAt: string;
  updatedByName: string | null;
};

export type WikiRevisionDto = {
  id: string;
  version: number;
  kind: WikiPageKind;
  title: string;
  aiUsage: WikiAiUsage;
  body: string;
  items: WikiQaItem[];
  editedByName: string | null;
  createdAt: string;
};

export type WikiSearchResultDto = {
  id: string;
  title: string;
  path: string;
  snippet: string;
};

type TreeNode = { id: string; parentId: string | null; position: number; title: string };

/**
 * The pages in reading order (each one before its sub-pages, sisters by
 * position), with the path of titles ("Offerta › Piano 39 €") and the depth
 * (1 = top). Pages whose mother is missing are left out.
 */
export function wikiTreeOrder<T extends TreeNode>(
  pages: readonly T[],
): Array<T & { path: string[]; depth: number }> {
  const children = new Map<string | null, T[]>();
  for (const page of pages) {
    const list = children.get(page.parentId) ?? [];
    list.push(page);
    children.set(page.parentId, list);
  }
  for (const list of children.values()) {
    list.sort((left, right) => left.position - right.position || left.id.localeCompare(right.id));
  }
  const ordered: Array<T & { path: string[]; depth: number }> = [];
  const visit = (parentId: string | null, path: string[], seen: Set<string>) => {
    for (const page of children.get(parentId) ?? []) {
      if (seen.has(page.id)) continue;
      seen.add(page.id);
      const pagePath = [...path, page.title];
      ordered.push({ ...page, path: pagePath, depth: pagePath.length });
      visit(page.id, pagePath, seen);
    }
  };
  visit(null, [], new Set());
  return ordered;
}

/** The page and all its sub-pages, by id. */
export function wikiSubtreeIds(
  pages: readonly { id: string; parentId: string | null }[],
  rootId: string,
): Set<string> {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const page of pages) {
      if (page.parentId && ids.has(page.parentId) && !ids.has(page.id)) {
        ids.add(page.id);
        grew = true;
      }
    }
  }
  return ids;
}

export const WIKI_PATH_SEPARATOR = " › ";

/** Question/answer pairs as text: "D: …" and "R: …", a blank line between pairs. */
export function wikiQaText(items: readonly Pick<WikiQaItem, "question" | "answer">[]): string {
  return items.map((item) => `D: ${item.question}\nR: ${item.answer}`).join("\n\n");
}

/**
 * The pairs back from their text (after "Modifica con AI"): every pair starts
 * with "D:" and has an "R:". Null when the text does not read as pairs.
 */
export function parseWikiQaText(text: string): Array<{ question: string; answer: string }> | null {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const pairs: Array<{ question: string; answer: string }> = [];
  for (const block of trimmed.split(/\n(?=D:)/u)) {
    const match = /^D:\s*([\s\S]*?)\n\s*R:\s*([\s\S]*)$/u.exec(block.trim());
    if (!match?.[1]?.trim() || !match[2]?.trim()) return null;
    pairs.push({ question: match[1].trim(), answer: match[2].trim() });
  }
  return pairs;
}

/** What the AI reads of a page: its Markdown, or its pairs as text. */
export function wikiPageText(page: {
  kind: WikiPageKind;
  body: string;
  items: readonly Pick<WikiQaItem, "question" | "answer">[];
}): string {
  return page.kind === "qa" ? wikiQaText(page.items) : page.body.trim();
}

/** How the "Quando serve" pieces are chosen (ADR 0075): kept with each proposal. */
export type WikiRetrievalRules = {
  /** Below this similarity a piece is about something else. */
  minScore: number;
  /** Only pieces within this distance of the best one. */
  margin: number;
  maxPieces: number;
  maxChars: number;
};

export const WIKI_RETRIEVAL_REASONS = [
  "used",
  "too_far",
  "far_from_best",
  "over_count",
  "over_chars",
] as const;
export type WikiRetrievalReason = (typeof WIKI_RETRIEVAL_REASONS)[number];

const retrievalRulesSchema = z.object({
  minScore: z.number(),
  margin: z.number(),
  maxPieces: z.number().int(),
  maxChars: z.number().int(),
});

const sourcePieceSchema = z
  .object({
    pageId: z.string(),
    key: z.string().default(""),
    hash: z.string().optional(),
    path: z.string(),
    heading: z.array(z.string()).catch([]).default([]),
    score: z.number(),
    reason: z.enum(WIKI_RETRIEVAL_REASONS).catch("used").default("used"),
    /** Only for the pieces used: what the AI read. */
    text: z.string().optional(),
  })
  .passthrough();

/**
 * What a proposal keeps of its prompt in `payload.aiSources` (ADR 0075). The
 * first version (20/10) had only the pieces used and the examples; the second
 * adds the searched texts and their vectors, the pieces left out with the
 * reason, the "Sempre" pages and the rules.
 */
export const proposalAiSourcesSchema = z.object({
  version: z.number().int().optional(),
  jobKey: z.string().optional(),
  /** The texts the wiki was searched with: the last messages, the lead's last words. */
  query: z.array(z.string()).catch([]).default([]),
  /** Their vectors (float32 in base64): the same search against today's wiki. */
  vectors: z.array(z.string()).catch([]).default([]),
  rules: retrievalRulesSchema.nullable().catch(null).default(null),
  instructionsChars: z.number().int().nullable().catch(null).default(null),
  always: z
    .array(z.object({ pageId: z.string(), path: z.string(), chars: z.number().int() }))
    .catch([])
    .default([]),
  wiki: z.array(sourcePieceSchema).catch([]),
  candidates: z.array(sourcePieceSchema).catch([]).default([]),
  examples: z
    .array(z.object({ messageId: z.string(), leadId: z.string(), score: z.number() }).passthrough())
    .catch([]),
});
export type ProposalAiSources = z.infer<typeof proposalAiSourcesSchema>;

/** A wiki piece in "Da dove viene": entered or left out, and how it is today. */
export type ProposalWikiPieceDto = {
  pageId: string;
  key: string;
  path: string;
  heading: string[];
  score: number;
  reason: WikiRetrievalReason;
  /** What the AI read for a piece used, otherwise the piece as it is today; null when gone. */
  text: string | null;
  /** The piece in today's wiki: the same, changed since, or no longer there. */
  today: "same" | "changed" | "removed";
  pageExists: boolean;
};

/**
 * "Da dove viene" of a proposal (ADR 0075): what entered its prompt besides the
 * conversation, what was left out and why, and what its AI job cost.
 */
export type ProposalSourcesDto = {
  /** False for proposals made before the wiki, or not by the AI. */
  available: boolean;
  /** Searched texts, pieces left out and "today": proposals since 21/10. */
  detailed: boolean;
  query: string[];
  rules: WikiRetrievalRules | null;
  instructionsChars: number | null;
  /** The "Sempre" pages, in every proposal. */
  always: Array<{ pageId: string; path: string; chars: number; exists: boolean }>;
  /** The "Quando serve" pieces, used and left out, nearest first. */
  wiki: ProposalWikiPieceDto[];
  /** Our replies to other leads in similar situations (ADR 0073). */
  examples: Array<{ leadId: string; leadName: string | null; reply: string | null; score: number }>;
  /** The OpenAI model that wrote the proposal, as OpenAI names it (ADR 0077). */
  model: string | null;
  /** Only for owners and admins; shared by the proposals made by the same job. */
  cost: {
    amount: number;
    currency: string;
    calls: number;
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    /** Every paid call of the job, oldest first: embeddings, searches, the proposal. */
    items: ApiUsageItemDto[];
  } | null;
};

/** The same search against today's wiki, to see what a change of the wiki does. */
export type ProposalSourcesTodayDto = {
  available: boolean;
  rules: WikiRetrievalRules;
  pieces: Array<{
    pageId: string;
    key: string;
    path: string;
    heading: string[];
    score: number;
    reason: WikiRetrievalReason;
    text: string;
    /** How the piece fared in the proposal; null when it was not among the candidates. */
    before: WikiRetrievalReason | null;
  }>;
  /** Pieces just written and not yet indexed: they are scored a few seconds later. */
  pending: number;
};

/** How our replies to other leads enter a proposal as examples (ADR 0073). */
export type AiExampleRules = { minScore: number; maxExamples: number };

export const AI_EXAMPLE_REASONS = ["used", "too_far", "over_count", "same_reply"] as const;
export type AiExampleReason = (typeof AI_EXAMPLE_REASONS)[number];

/**
 * The chats like this one whose replies the AI could take as examples: the
 * nearest ones in the index of today, among the replies sent before the
 * proposal to other leads, with whether they enter and why not.
 */
export type ProposalSourcesExamplesDto = {
  /** False when the proposal did not keep the vector of its search (before 22/10). */
  available: boolean;
  /** The examples are switched on in the workspace settings. */
  enabled: boolean;
  rules: AiExampleRules;
  examples: Array<{
    messageId: string;
    leadId: string;
    leadName: string | null;
    sentAt: string;
    /** The messages before our reply, names and contacts hidden. */
    situation: string;
    reply: string;
    score: number;
    /** What the rules say today. */
    reason: AiExampleReason;
    /** The reply was in the prompt of the proposal. */
    used: boolean;
  }>;
};
